import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Course, Section, Lesson } from '@/lib/types';
import CertificateRequirements from '@/components/certificates/CertificateRequirements';

interface CourseLandingProps {
    course: Course;
    sections: Section[];
    lessons: Lesson[];
    onEnroll: () => void;
    enrolling?: boolean;
}

function formatDuration(minutes: number): string {
    if (minutes < 60) return `${minutes} min`;
    const hours = Math.floor(minutes / 60);
    const rest = minutes % 60;
    return rest ? `${hours}h ${rest}m` : `${hours}h`;
}

function instructorName(course: Course): string {
    const fromParts = [course.tutor?.first_name, course.tutor?.last_name].filter(Boolean).join(' ');
    return course.tutor_name || fromParts || 'Instructor';
}

function instructorInitials(course: Course): string {
    const first = course.tutor?.first_name?.charAt(0) || '';
    const last = course.tutor?.last_name?.charAt(0) || '';
    return (first + last) || 'I';
}

function lessonsForSection(lessons: Lesson[], sectionId: number): Lesson[] {
    return lessons.filter(l => l.section === sectionId).sort((a, b) => a.order - b.order);
}

export default function CourseLanding({
    course,
    sections,
    lessons,
    onEnroll,
    enrolling = false,
}: CourseLandingProps) {
    const [openSectionIds, setOpenSectionIds] = useState<number[]>(() =>
        sections[0] ? [sections[0].id] : []
    );

    const totalMinutes = lessons.reduce((sum, l) => sum + (l.duration_minutes || 0), 0);
    const name = instructorName(course);
    const initials = instructorInitials(course);
    const visibilityLabel = course.visibility === 'private' ? 'Private' : 'Public';

    const toggleSection = (id: number) => {
        setOpenSectionIds(prev =>
            prev.includes(id) ? prev.filter(s => s !== id) : [...prev, id]
        );
    };

    const stats = [
        { label: 'Sections', value: String(sections.length) },
        { label: 'Lessons', value: String(lessons.length) },
        ...(totalMinutes > 0 ? [{ label: 'Duration', value: formatDuration(totalMinutes) }] : []),
    ];

    return (
        <div className="min-h-full bg-slate-50">
            <div className="mx-auto max-w-6xl px-4 py-6 sm:px-6 lg:px-8">
                <Link
                    to="/courses"
                    className="inline-flex items-center gap-1.5 text-xs font-medium text-slate-500 hover:text-slate-800"
                >
                    <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                    Back to courses
                </Link>

                <div className="mt-4 overflow-hidden rounded-2xl border border-slate-100 bg-slate-900 shadow-sm">
                    <div className="relative aspect-[21/9] min-h-[180px] w-full">
                        {course.cover_image_url ? (
                            <img
                                src={course.cover_image_url}
                                alt=""
                                className="absolute inset-0 h-full w-full object-cover"
                            />
                        ) : (
                            <div className="absolute inset-0 bg-gradient-to-br from-blue-700 via-blue-600 to-teal-600" />
                        )}
                        <div className="absolute inset-0 bg-gradient-to-t from-slate-950/80 via-slate-950/25 to-transparent" />
                        <div className="absolute inset-x-0 bottom-0 p-5 sm:p-8">
                            <p className="text-[10px] font-semibold uppercase tracking-wide text-amber-300">Course</p>
                            <h1 className="mt-1 text-xl font-bold tracking-tight text-white sm:text-2xl">
                                {course.title}
                            </h1>
                            <p className="mt-1.5 text-sm text-slate-200">By {name}</p>
                        </div>
                    </div>
                </div>

                <div className="mt-3 flex flex-wrap gap-1.5">
                    <span className="inline-flex items-center rounded-full bg-sky-50 px-2.5 py-0.5 text-[11px] font-semibold text-sky-800 ring-1 ring-inset ring-sky-200">
                        {visibilityLabel}
                    </span>
                    {stats.map(s => (
                        <span
                            key={s.label}
                            className="inline-flex items-center rounded-full bg-white px-2.5 py-0.5 text-[11px] font-medium text-slate-600 ring-1 ring-inset ring-slate-200"
                        >
                            {s.value} {s.label.toLowerCase()}
                        </span>
                    ))}
                </div>

                <div className="mt-8 grid grid-cols-1 gap-8 lg:grid-cols-3">
                    <div className="order-2 space-y-8 lg:order-1 lg:col-span-2">
                        <section>
                            <h2 className="text-base font-semibold text-slate-900">About this course</h2>
                            {course.description?.trim() ? (
                                <p className="mt-3 whitespace-pre-wrap text-sm leading-relaxed text-slate-600">
                                    {course.description}
                                </p>
                            ) : (
                                <p className="mt-3 text-sm text-slate-400">No description yet.</p>
                            )}
                        </section>

                        <section>
                            <h2 className="text-base font-semibold text-slate-900">Curriculum</h2>
                            {sections.length === 0 ? (
                                <p className="mt-3 text-sm text-slate-400">No sections published yet.</p>
                            ) : (
                                <div className="mt-3 overflow-hidden rounded-xl border border-slate-200 bg-white">
                                    {sections.map(section => {
                                        const sectionLessons = lessonsForSection(lessons, section.id);
                                        const isOpen = openSectionIds.includes(section.id);
                                        return (
                                            <div key={section.id} className="border-b border-slate-100 last:border-b-0">
                                                <button
                                                    type="button"
                                                    onClick={() => toggleSection(section.id)}
                                                    className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left hover:bg-slate-50"
                                                    aria-expanded={isOpen}
                                                >
                                                    <span className="min-w-0">
                                                        <span className="block truncate text-sm font-medium text-slate-900">
                                                            {section.title}
                                                        </span>
                                                        <span className="text-xs text-slate-400">
                                                            {sectionLessons.length} lesson{sectionLessons.length !== 1 ? 's' : ''}
                                                        </span>
                                                    </span>
                                                    <svg
                                                        className={`h-4 w-4 shrink-0 text-slate-400 transition-transform ${isOpen ? 'rotate-180' : ''}`}
                                                        fill="none"
                                                        viewBox="0 0 24 24"
                                                        stroke="currentColor"
                                                    >
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                                    </svg>
                                                </button>
                                                {isOpen && (
                                                    <ul className="border-t border-slate-100 bg-slate-50/70 px-4 py-2">
                                                        {sectionLessons.length === 0 ? (
                                                            <li className="py-2 text-xs text-slate-400">No published lessons in this section.</li>
                                                        ) : (
                                                            sectionLessons.map(lesson => (
                                                                <li
                                                                    key={lesson.id}
                                                                    className="flex items-center justify-between gap-3 py-2 text-sm text-slate-700"
                                                                >
                                                                    <span className="min-w-0 truncate">{lesson.title}</span>
                                                                    {lesson.duration_minutes ? (
                                                                        <span className="shrink-0 text-xs text-slate-400">
                                                                            {formatDuration(lesson.duration_minutes)}
                                                                        </span>
                                                                    ) : null}
                                                                </li>
                                                            ))
                                                        )}
                                                    </ul>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                            <p className="mt-2 text-xs text-slate-400">Enroll to open lessons and track your progress.</p>
                        </section>
                    </div>

                    <aside className="order-1 lg:order-2 lg:col-span-1">
                        <div className="rounded-xl border border-slate-100 bg-white p-4 shadow-sm lg:sticky lg:top-6">
                            {course.cover_image_url && (
                                <img
                                    src={course.cover_image_url}
                                    alt=""
                                    className="mb-3 h-28 w-full rounded-lg object-cover"
                                />
                            )}
                            <button
                                type="button"
                                onClick={onEnroll}
                                disabled={enrolling}
                                className="w-full rounded-lg bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-blue-700 disabled:opacity-60"
                            >
                                {enrolling ? 'Enrolling…' : 'Enroll now'}
                            </button>
                            <dl className="mt-4 space-y-2 text-sm">
                                {stats.map(s => (
                                    <div key={s.label} className="flex items-center justify-between">
                                        <dt className="text-slate-500">{s.label}</dt>
                                        <dd className="font-medium text-slate-900">{s.value}</dd>
                                    </div>
                                ))}
                            </dl>
                            <div className="mt-4">
                                <CertificateRequirements course={course} compact />
                            </div>
                            <div className="mt-4 flex items-center gap-3 border-t border-slate-100 pt-4">
                                <div className="flex h-9 w-9 items-center justify-center rounded-full bg-blue-600 text-xs font-semibold text-white">
                                    {initials}
                                </div>
                                <div className="min-w-0">
                                    <p className="text-[11px] font-medium uppercase tracking-wide text-slate-400">Instructor</p>
                                    <p className="truncate text-sm font-medium text-slate-900">{name}</p>
                                </div>
                            </div>
                        </div>
                    </aside>
                </div>
            </div>
        </div>
    );
}
